import { MatchEvent, MatchToEventsMap } from "../../directus/collections";
import { average } from "../utils";
import { getChallengeStartingTime, isChallengeHintEvent } from "./enigma";

/**
 *  Compute the time offsets between the base time and each event's creation time.
 *  Note: the events will not be sorted.
 *
 * @param baseTime - The base time in milliseconds from which to compute the offsets.
 * @param events - An array of MatchEvent objects.
 * Each event should have a `date_created` property in ISO format.
 * @returns An array of time offsets in milliseconds for each event.
 */
export function computeEventsTimeOffsets(
  baseTime: number,
  events: MatchEvent[]
): number[] {
  return events.map(
    (event) => new Date(event.date_created).getTime() - baseTime
  );
}
/**
 *  Computes the time offsets of hints for multiple matches.
 * * @param events - An object where keys are match IDs and values are arrays of MatchEvent objects.
 * * @returns An array of arrays, where each inner array contains time offsets for hints in a specific match.
 * * @throws Error if given events for a match do not effectively belong to the same match or challenge.
 * * @example
 */
export function bulkComputeHintTimeOffsets(
  events: MatchToEventsMap
): number[][] {
  return Object.values(events).reduce((acc, events) => {
    const challengeStartingTime = getChallengeStartingTime(events);
    const hints = events.filter((event) => isChallengeHintEvent(event));
    return [...acc, computeEventsTimeOffsets(challengeStartingTime, hints)];
  }, [] as number[][]);
}
/**
 *
 * Calculates the average of each column in a 2D dataset.
 * * This function takes a 2D array of numbers and computes the average for each column.
 * * @param dataset - A 2D array of numbers where each inner array represents a row of data.
 * @returns An array of numbers representing the average of each column.
 *
 * @example
 * ```typescript
 * const dataset = [
 *     [1, 2, 3],
 *     [4, 5, 6],
 *     [7, 8, 9],
 *     [10, 11, 12]
 * ];
 * const averages = calculateColumnAverages(dataset);
 * console.log(averages); // Output: [5.5, 6.5, 7.5]
 */
export function calculateColumnAverages(dataset: (number | null)[][]) {
  const maxLength = Math.max(...dataset.map((d) => d.length));
  const aggregates: number[][] = [];

  for (let i = 0; i < maxLength; i++) {
    aggregates[i] = [];
    for (let row of dataset) {
      const value = row[i];
      if (value != null) {
        aggregates[i].push(value);
      }
    }
  }
  return aggregates.map(
    (row) => row.reduce((sum, value) => sum + value, 0) / row.length
  );
}
/**
 * Groups set of objects by a given key and sorts them using the provided sorting function.
 * No sorting is applied to the dataset.
 *
 * @param key - A function that extracts the key from an object.
 * @param entries - The entries to group.
 * @returns A Record<string, T[]> of grouped entries.
 */
export const groupEntries = <T>(
  entries: Iterable<T>,
  key: (event: T) => string | undefined | null
) => {
  const grouped: Record<string, T[]> = {};

  for (const entry of entries) {
    const eventKey = key(entry);
    if (!eventKey) {
      continue;
    }
    if (!grouped[eventKey]) {
      grouped[eventKey] = [];
    }
    grouped[eventKey].push(entry);
  }
  return grouped;
};

/**
 * Maps a set of objects by a given key.
 *
 * @param key - A function that extracts the key from an object.
 * @param entries - The entries to map.
 * @param options { onConflict } - Optional conflict resolution strategy.
 * @returns A Record<string, T> of mapped entries.
 */
export const mapEntries = <T, K extends string | number | symbol>(
  entries: Iterable<T>,
  key: (event: T) => K | undefined | null,
  { onConflict }: { onConflict?: (existing: T, newEntry: T) => T } = {}
) => {
  const entriesMap: Record<K, T> = {} as Record<K, T>;

  for (const entry of entries) {
    const eventKey = key(entry);
    if (eventKey) {
      if (entriesMap[eventKey] && onConflict) {
        entriesMap[eventKey] = onConflict(entriesMap[eventKey], entry);
      } else {
        entriesMap[eventKey] = entry;
      }
    }
  }
  return entriesMap;
};

/**
 * Calculates the time difference between two events in milliseconds.
 *
 * @param a - The first event.
 * @param b - The second event.
 * @returns The time difference in milliseconds.
 */
export function calculateEventsTimeDiffs(a: MatchEvent, b: MatchEvent): number {
  return calculateStringDatesTimeDiffs(a.date_created, b.date_created);
}

export function calculateStringDatesTimeDiffs(
  a?: string,
  b?: string,
  defaultValue: number = 0
): number {
  if (!a || !b) return defaultValue;
  return new Date(a).getTime() - new Date(b).getTime();
}

export function calculateStringDatesTimeAvg<T extends object>(
  items: T[],
  keys: {
    start: (item: T) => string | undefined;
    end: (item: T) => string | undefined;
  }
): number {
  const durations: number[] = [];
  for (const item of items) {
    const start = keys.start(item);
    const end = keys.end(item);
    if (start && end) {
      durations.push(calculateStringDatesTimeDiffs(end, start));
    }
  }
  return average(durations);
}

export function formatPercentage(
  value: number,
  total: number,
  options?: { fractionDigits?: number }
): string {
  if (total === 0) return "0%";

  const fractionDigits = options?.fractionDigits ?? 2;
  return ((value / total) * 100).toFixed(fractionDigits) + "%";
}
